import { FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

const MethodologyPDF = () => {
  const generatePDF = () => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = 20;

      // Title
      pdf.setFontSize(20);
      pdf.setFont("helvetica", "bold");
      pdf.text("Walking Weight Loss Calculator", margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      pdf.text("Scientific Methodology & Citations", margin, yPosition);
      yPosition += 15;

      // Creator and Reviewer
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.text("Created by: ", margin, yPosition);
      pdf.setFont("helvetica", "normal");
      pdf.text("Shafi Tareen (Founder, Datalevo)", margin + 25, yPosition);
      yPosition += 6;

      pdf.setFont("helvetica", "bold");
      pdf.text("Reviewed by: ", margin, yPosition);
      pdf.setFont("helvetica", "normal");
      pdf.text("Nasir Khan, M.Sc. (Exercise Physiology)", margin + 25, yPosition);
      yPosition += 6;

      pdf.setFont("helvetica", "bold");
      pdf.text("Version: ", margin, yPosition);
      pdf.setFont("helvetica", "normal");
      pdf.text("1.0.0 (November 22, 2025)", margin + 25, yPosition);
      yPosition += 15;

      // ACSM Formula
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text("1. ACSM Walking Equation", margin, yPosition);
      yPosition += 7;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      const acsmText = [
        "The American College of Sports Medicine (ACSM) walking equation calculates energy",
        "expenditure based on speed, body weight, and grade (incline).",
        "",
        "Formula: VO2 = (0.1 × speed) + (1.8 × speed × grade) + 3.5",
        "Where: speed in m/min, grade as decimal (e.g., 5% = 0.05)",
        "",
        "Citation: ACSM's Guidelines for Exercise Testing and Prescription, 10th Edition (2018)"
      ];
      acsmText.forEach(line => {
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      });
      yPosition += 5;

      // MET Method
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text("2. MET (Metabolic Equivalent) Method", margin, yPosition);
      yPosition += 7;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      const metText = [
        "Uses standardized MET values from the Compendium of Physical Activities.",
        "MET values vary based on walking speed and intensity.",
        "",
        "Formula: Calories = MET × weight (kg) × duration (hours)",
        "",
        "Walking MET values:",
        "• Slow (2.0 mph / 3.2 km/h): 2.0 METs",
        "• Moderate (3.0 mph / 4.8 km/h): 3.5 METs",
        "• Brisk (4.0 mph / 6.4 km/h): 5.0 METs",
        "• Very brisk (4.5 mph / 7.2 km/h): 6.3 METs",
        "",
        "Citation: Ainsworth BE, et al. Compendium of Physical Activities (2011)"
      ];
      metText.forEach(line => {
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      });
      yPosition += 5;

      // Check if new page needed
      if (yPosition > 240) {
        pdf.addPage();
        yPosition = 20;
      }

      // Mifflin-St Jeor
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text("3. Mifflin-St Jeor Equation (BMR/RMR)", margin, yPosition);
      yPosition += 7;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      const mifflinText = [
        "Calculates Basal Metabolic Rate (BMR) for total daily energy expenditure.",
        "",
        "Formula (Men): BMR = 10 × weight(kg) + 6.25 × height(cm) - 5 × age + 5",
        "Formula (Women): BMR = 10 × weight(kg) + 6.25 × height(cm) - 5 × age - 161",
        "",
        "Activity multipliers:",
        "• Sedentary (little/no exercise): BMR × 1.2",
        "• Light (1-3 days/week): BMR × 1.375",
        "• Moderate (3-5 days/week): BMR × 1.55",
        "• Very active (6-7 days/week): BMR × 1.725",
        "",
        "Citation: Mifflin MD, et al. A new predictive equation for resting energy",
        "expenditure in healthy individuals. Am J Clin Nutr. 1990;51(2):241-7"
      ];
      mifflinText.forEach(line => {
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      });
      yPosition += 5;

      // Katch-McArdle
      if (yPosition > 230) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text("4. Katch-McArdle Formula (Optional)", margin, yPosition);
      yPosition += 7;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      const katchText = [
        "More accurate BMR calculation when body fat percentage is known.",
        "",
        "Formula: BMR = 370 + (21.6 × lean body mass in kg)",
        "Where: Lean Body Mass = weight × (1 - body fat %)",
        "",
        "This formula accounts for muscle mass, which burns more calories at rest.",
        "",
        "Citation: Katch VL, McArdle WD. Introduction to Nutrition, Exercise,",
        "and Health. 4th ed. Lippincott Williams & Wilkins; 1993"
      ];
      katchText.forEach(line => {
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      });
      yPosition += 10;

      // Weight Loss Assumptions
      if (yPosition > 230) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text("5. Weight Loss Calculations", margin, yPosition);
      yPosition += 7;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      const weightLossText = [
        "Standard caloric deficit calculation:",
        "• 1 kg fat = approximately 7,700 kcal",
        "• 1 lb fat = approximately 3,500 kcal",
        "",
        "Weight change = (Net caloric deficit) / (7700 kcal/kg)",
        "",
        "Safe weight loss recommendations:",
        "• 0.5-1 kg (1-2 lbs) per week for sustainable results",
        "• Maximum recommended deficit: 1000 kcal/day",
        "",
        "Citations:",
        "• Hall KD, et al. Energy balance and its components. Am J Clin Nutr. 2012",
        "• NHS Weight Loss Guide (www.nhs.uk)",
        "• National Institute of Diabetes and Digestive and Kidney Diseases (NIDDK)"
      ];
      weightLossText.forEach(line => {
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      });
      yPosition += 10;

      // Disclaimer
      if (yPosition > 240) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.text("Important Disclaimer", margin, yPosition);
      yPosition += 7;

      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      const disclaimerLines = pdf.splitTextToSize(
        "These calculations provide estimates based on scientific formulas and peer-reviewed research. " +
        "Individual results may vary due to factors such as genetics, metabolism, medical conditions, " +
        "and adherence to the program. Always consult with a healthcare professional before starting " +
        "any weight loss or exercise program, especially if you have existing health conditions.",
        maxWidth
      );
      disclaimerLines.forEach((line: string) => {
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      });

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(128);
      pdf.text("© 2025 Datalevo | Walking Weight Loss Calculator v1.0.0", margin, 285);
      pdf.text("Generated: " + new Date().toLocaleDateString(), pageWidth - margin - 40, 285);

      // Save PDF
      pdf.save("Walking-Calculator-Methodology.pdf");
      toast.success("Methodology PDF downloaded");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  return (
    <Button onClick={generatePDF} variant="outline" size="sm" className="gap-2">
      <FileDown className="h-4 w-4" />
      Download Methodology PDF
    </Button>
  );
};

export default MethodologyPDF;